import type { Lang } from '@/lib/i18n';

export type Benefit = { ico: string; name: string; desc: string; bullets: string[] };

export type WebsiteDict = {
  page_title: string;
  page_desc: string;
  nav_home: string; nav_product: string;
  nav_signin: string; nav_signup: string;
  hero_badge: string;
  hero_title_a: string; hero_title_b: string;
  hero_desc: string;
  hero_cta: string; hero_demo: string;
  hero_note: string;
  trust_title: string;
  trust: ReadonlyArray<readonly [string, string]>;
  bench_kicker: string;
  bench_title: string;
  benefits: Benefit[];
  how_kicker: string; how_title: string;
  how_steps: ReadonlyArray<readonly [string, string]>;
  feat_kicker: string; feat_title: string;
  feat: ReadonlyArray<readonly [string, string, string]>;
  faq_kicker: string; faq_title: string;
  faqs: ReadonlyArray<readonly [string, string]>;
  cta_title: string; cta_sub: string; cta_btn: string;
  foot_tag: string;
  foot_product: string; foot_links_p: ReadonlyArray<readonly [string, string]>;
  foot_company: string; foot_links_c: ReadonlyArray<readonly [string, string]>;
  foot_help: string;    foot_links_h: ReadonlyArray<readonly [string, string]>;
  foot_legal: string;
  foot_copy: string;
};

export const I18N: Record<Lang, WebsiteDict> = {
  en: {
    page_title: 'Invosly — Smart & Simple Invoicing for Thai Businesses',
    page_desc:  'Issue Tax Invoices in 3 steps with built-in VAT, WHT and e-Tax compliance. Trilingual UI, mobile-ready PWA, full PDPA compliance.',
    nav_home: 'Home', nav_product: 'Product',
    nav_signin: 'Sign in', nav_signup: 'Start free',
    hero_badge: 'Thai Revenue Department · e-Tax compliant',
    hero_title_a: 'Smart & Simple', hero_title_b: 'invoicing for Thai businesses',
    hero_desc: '3 steps to issue a Tax Invoice. Built-in VAT calculation, e-Tax compliant, print and one-click share.',
    hero_cta: 'Get started free →', hero_demo: 'See product',
    hero_note: 'No credit card · Cancel anytime · Setup in 60 seconds',
    trust_title: 'Trusted by Thai SMEs and Chinese-Thai businesses',
    trust: [
      ['100%', 'RD Section 86 compliant'],
      ['6',    'Document types — Quote → Receipt'],
      ['3',    'Languages — TH / EN / 中'],
      ['256',  'AES-bit field encryption'],
    ],
    bench_kicker: 'WHY INVOSLY',
    bench_title:  'Five reasons businesses choose us',
    benefits: [
      { ico: '✓', name: 'Fully compliant',
        desc: 'Every Tax Invoice covers all 13 RD Section 86 mandatory fields. VAT 7%, WHT (1/3/5/10/15%), and PDF/A-1b for long-term archiving — built-in.',
        bullets: ['RD Section 86 mandatory fields', 'VAT inclusive / exclusive modes', 'WHT certificate (Form 50 บิส)', 'PDF/A-1b archival format'] },
      { ico: '⚡', name: 'Simple to use',
        desc: 'Issue a Tax Invoice in 30 seconds. Pick a customer (or scan their QR card), enter the amount, send. No accounting knowledge needed.',
        bullets: ['3-step workflow', 'Customer auto-fill from history', 'Mobile-first responsive UI', 'Zero training needed'] },
      { ico: '🔒', name: 'Bank-grade security',
        desc: 'AES-256 field encryption on Tax IDs and bank accounts. PDPA compliant. Hosted in Thailand. Audit logs retained 7 years per tax law.',
        bullets: ['AES-256 encryption', 'PDPA compliant', 'Thailand-hosted (AWS Singapore)', '7-year audit retention'] },
      { ico: '🔄', name: 'Smart document workflow',
        desc: 'A complete document automation engine. Six document types (Quotation / Invoice / Delivery Order / Tax Invoice / Receipt / WHT Certificate) chain together with one-click conversion. Three business flow modes (A / B / C) match the way Thai SMEs, B2B firms and project businesses actually work.',
        bullets: ['6 document types in one chain', '1-click Quote → Invoice → Tax Invoice', 'Three flow modes (A/B/C) for SME/B2B/projects', 'Frozen PDF templates protect old documents'] },
      { ico: '🤖', name: 'AI-powered tax tools',
        desc: 'Snap a photo of a 50 ทวิ certificate — Claude Vision auto-extracts payer, payee, income type, base amount and WHT rate. Customer book and item library de-duplicate automatically. Multi-copy PDF output (Original × N + Copy × M) in one render.',
        bullets: ['WHT certificate OCR (Claude Vision)', 'Auto-dedupe customers + items', 'Multi-copy PDF (Original / Copy)', 'Per-card invoice number prefix'] },
    ],
    how_kicker: 'HOW IT WORKS',
    how_title:  'From sign-up to your first invoice — 60 seconds',
    how_steps: [
      ['Sign up free',         'Email + password. No credit card. We build your account, your default Invoice Card and your first organization.'],
      ['Add a customer',       'Type Tax ID once (or scan their QR card) — name, address and email auto-fill from your customer book.'],
      ['Issue & share',        'Enter line items, click Send. We render a print-ready Tax Invoice PDF and deliver via Email, LINE or download.'],
    ],
    feat_kicker: 'PRODUCT HIGHLIGHTS',
    feat_title:  'Built specifically for the way Thai businesses invoice',
    feat: [
      ['📇', 'Invoice Card + QR',  'Industry-first. Your Tax ID, address and bank details are wrapped in a QR code — clients scan once, never re-type.'],
      ['💸', 'PromptPay embedded', 'Every invoice carries a PromptPay QR with the amount pre-filled. Customer scans, you reconcile automatically.'],
      ['📄', 'Print-grade PDF',    'WeasyPrint engine + Sarabun + Noto Sans Thai fonts. A4, 300 DPI, embedded fonts — looks identical on every printer.'],
      ['🔍', 'Public detail page', 'Each invoice carries a tamper-proof QR linking to a public detail page — your clients verify authenticity in one tap.'],
      ['📱', 'Mobile + desktop',   'PWA on mobile, full SPA on desktop, admin console for ops — same data, all in sync.'],
      ['🛡', 'Audit & PDPA',       'Every action logged. One-click data export and deletion APIs for PDPA data subject requests.'],
    ],
    faq_kicker: 'FAQ',
    faq_title:  'Frequently asked questions',
    faqs: [
      ['Is Invosly compliant with the Thai Revenue Department?',
       'Yes. Every Tax Invoice template covers all mandatory fields under RD Section 86 — bilingual title, seller / buyer Tax IDs, branch, VAT 7% line, original/copy marker. We also align with the e-Tax Invoice by Email programme and offer PDF/A-1b archival output.'],
      ['Do I have to issue documents in a fixed order?',
       'No. Six document types (Quotation, Invoice, Delivery Order, Tax Invoice, Receipt, WHT Certificate) can each be created independently or chained together. Three business flow modes (A: Invoice → Tax Invoice; B: direct Tax Invoice; C: deposit Invoice → Tax Invoice) match the way Thai SMEs, B2B firms and project businesses actually work.'],
      ['Where is my data stored? Is it PDPA compliant?',
       'Data is hosted in AWS Singapore with Thailand-region failover. Tax IDs and bank account numbers are AES-256 field-encrypted at rest. We provide one-click data export and deletion APIs for PDPA data subject requests, and retain audit logs for 7 years as required by Thai tax law.'],
      ['Can I issue invoices from my phone?',
       'Yes. The mobile interface is a PWA — install it from your browser to your home screen. Offline drafts sync automatically when you reconnect.'],
      ['What is the "Invoice Card QR" and how is it different?',
       'It is your tax identity wrapped in a QR code. When a client needs to invoice you, they scan once and your Tax ID, address, branch and PromptPay info auto-fill into their form. Industry-first feature — only Invosly has it today.'],
      ['Can I cancel anytime?',
       'Yes — no contracts, no cancellation fees. Cancel from Settings; your account becomes read-only at the next billing cycle and you can export all your data.'],
    ],
    cta_title: 'Start invoicing in 60 seconds',
    cta_sub:   'Built for Thai SMEs. Trilingual UI, fully PDPA compliant, mobile-ready PWA.',
    cta_btn:   'Create your free account →',
    foot_tag: 'Smart invoicing built for Thai Revenue Department compliance, by a team that ships.',
    foot_product:  'Product',  foot_links_p: [['Home','/'], ['Product','/product']],
    foot_company:  'Company',  foot_links_c: [['About','#'],  ['Blog','#'],  ['Contact','#']],
    foot_help:     'Help',     foot_links_h: [['Docs','#'],   ['FAQ','#'],   ['System status','#']],
    foot_legal:    'Privacy · Terms',
    foot_copy:     'Invosly Co., Ltd. · All rights reserved',
  },
  th: {
    page_title: 'Invosly — ระบบใบกำกับภาษีสำหรับธุรกิจไทย',
    page_desc:  'ออกใบกำกับภาษีใน 3 ขั้นตอน คำนวณ VAT, WHT และ e-Tax อัตโนมัติ UI 3 ภาษา PWA มือถือ PDPA ครบถ้วน',
    nav_home: 'หน้าแรก', nav_product: 'สินค้า',
    nav_signin: 'เข้าสู่ระบบ', nav_signup: 'เริ่มฟรี',
    hero_badge: 'รองรับ e-Tax กรมสรรพากร',
    hero_title_a: 'Smart & Simple', hero_title_b: 'สำหรับธุรกิจไทย',
    hero_desc: 'ออกใบกำกับภาษี 3 ขั้นตอน คำนวณ VAT อัตโนมัติ ตรงตามมาตรฐาน e-Tax พิมพ์และแชร์ได้ในคลิกเดียว',
    hero_cta: 'เริ่มต้นฟรี →', hero_demo: 'ดูฟีเจอร์',
    hero_note: 'ไม่ต้องใช้บัตรเครดิต · ยกเลิกเมื่อไหร่ก็ได้ · ตั้งค่าเสร็จใน 60 วินาที',
    trust_title: 'เชื่อมั่นโดย SME ไทยและธุรกิจจีน-ไทย',
    trust: [
      ['100%', 'รองรับมาตรา 86'],
      ['6',    'ประเภทเอกสาร — เสนอราคา → ใบเสร็จ'],
      ['3',    'ภาษา TH / EN / 中'],
      ['256',  'AES-bit เข้ารหัส'],
    ],
    bench_kicker: 'ทำไมต้อง INVOSLY', bench_title: '5 เหตุผลที่ธุรกิจเลือกเรา',
    benefits: [
      { ico:'✓', name:'รองรับเต็มรูปแบบ', desc:'ใบกำกับภาษีทุกใบครอบคลุม 13 ฟิลด์ตามมาตรา 86 รองรับ VAT 7% และ WHT (1/3/5/10/15%) ส่งออก PDF/A-1b เก็บถาวร', bullets:['ฟิลด์บังคับมาตรา 86','VAT include/exclude','ใบ 50 ทวิ (WHT)','PDF/A-1b เก็บถาวร'] },
      { ico:'⚡', name:'ใช้ง่าย', desc:'ออกใบกำกับภาษีใน 30 วินาที เลือกลูกค้า (หรือสแกนนามบัตร QR) ใส่จำนวนเงิน ส่ง — ไม่ต้องรู้บัญชี', bullets:['3 ขั้นตอน','ลูกค้า auto-fill','UI Mobile-first','เรียนรู้เป็นศูนย์'] },
      { ico:'🔒', name:'ปลอดภัยระดับธนาคาร', desc:'เข้ารหัส AES-256 บนเลขผู้เสียภาษีและบัญชีธนาคาร PDPA ครบ ติดตั้งในไทย เก็บ audit log 7 ปีตามกฎหมาย', bullets:['เข้ารหัส AES-256','PDPA ครบ','ติดตั้งในไทย','Audit 7 ปี'] },
      { ico:'🔄', name:'เครื่องยนต์เอกสารอัจฉริยะ', desc:'ระบบเอกสารครบวงจร 6 ประเภท (ใบเสนอราคา / Invoice / ใบส่งของ / ใบกำกับภาษี / ใบเสรับ / ใบ 50 ทวิ) เชื่อมต่อกันคลิกเดียวแปลง รองรับ 3 รูปแบบธุรกิจ (A/B/C) ตามวิธีทำงานจริงของ SME B2B และโครงการ', bullets:['6 ประเภทเอกสารในสายเดียว','คลิกเดียว Quote → Invoice → Tax Invoice','3 flow mode (A/B/C) สำหรับ SME/B2B/โครงการ','PDF template ล็อคไว้ ไม่กระทบเอกสารเก่า'] },
      { ico:'🤖', name:'เครื่องมือภาษีด้วย AI', desc:'ถ่ายรูปใบ 50 ทวิ — Claude Vision อ่านข้อมูลผู้จ่าย ผู้รับ ประเภทรายได้ ฐานเงิน อัตรา WHT อัตโนมัติ Customer book และ item library ลบซ้ำเอง PDF หลายสำเนา (Original × N + Copy × M) ในการ render เดียว', bullets:['WHT OCR (Claude Vision)','Auto-dedupe ลูกค้า + รายการ','PDF Multi-copy (Original / Copy)','Prefix เลขใบต่อนามบัตร'] },
    ],
    how_kicker:'วิธีใช้งาน', how_title:'จากสมัครถึงใบแรก — 60 วินาที',
    how_steps: [
      ['สมัครฟรี','อีเมล + รหัสผ่าน ไม่ต้องใช้บัตรเครดิต ระบบสร้างบัญชี นามบัตรเริ่มต้น และองค์กรแรกให้คุณทันที'],
      ['เพิ่มลูกค้า','พิมพ์เลขผู้เสียภาษีครั้งเดียว (หรือสแกน QR นามบัตร) — ชื่อ ที่อยู่ อีเมล จะ auto-fill จาก customer book'],
      ['ออก & แชร์','กรอกรายการ คลิกส่ง ระบบสร้าง PDF พร้อมพิมพ์ และส่งผ่าน Email LINE หรือดาวน์โหลด'],
    ],
    feat_kicker:'ฟีเจอร์เด่น', feat_title:'ออกแบบมาเฉพาะวิธีออกใบของธุรกิจไทย',
    feat: [
      ['📇','นามบัตร Invoice + QR','ครั้งแรกในวงการ เลขผู้เสียภาษี ที่อยู่ และข้อมูลธนาคารของคุณห่อใน QR — ลูกค้าสแกนครั้งเดียว ไม่ต้องพิมพ์ซ้ำ'],
      ['💸','PromptPay ในตัว','ทุกใบมี QR PromptPay กรอกจำนวนพร้อม ลูกค้าสแกน คุณกระทบยอดอัตโนมัติ'],
      ['📄','PDF คุณภาพพิมพ์','WeasyPrint + Sarabun + Noto Sans Thai · A4, 300 DPI, ฝัง font — เหมือนกันทุกเครื่อง'],
      ['🔍','หน้าตรวจสอบสาธารณะ','ใบกำกับทุกใบมี QR กันปลอม ลิงก์ไปหน้าสาธารณะให้ลูกค้ายืนยันได้ทันที'],
      ['📱','Mobile + Desktop','PWA บนมือถือ SPA เต็มรูปแบบบน Desktop คอนโซลผู้ดูแล — ข้อมูลเดียวกัน ซิงก์ครบ'],
      ['🛡','Audit & PDPA','บันทึกทุก action API export/delete data ในคลิกเดียว สำหรับคำขอผู้บริโภค PDPA'],
    ],
    faq_kicker:'คำถามที่พบบ่อย', faq_title:'คำถามที่พบบ่อย',
    faqs: [
      ['Invosly รองรับมาตรฐานกรมสรรพากรไหม?', 'รองรับเต็มรูปแบบ ทุกใบกำกับภาษีครอบคลุมฟิลด์บังคับตามมาตรา 86 — หัวภาษาคู่ ผู้ขาย/ผู้ซื้อ Tax ID สาขา VAT 7% ระบุต้นฉบับ/สำเนา รองรับ e-Tax Invoice by Email และส่งออก PDF/A-1b'],
      ['ต้องออกเอกสารตามลำดับที่กำหนดไหม?', 'ไม่ต้อง 6 ประเภทเอกสาร (ใบเสนอราคา, Invoice, ใบส่งของ, ใบกำกับภาษี, ใบเสร็จ, ใบ 50 ทวิ) สามารถสร้างเดี่ยวๆ หรือเชื่อมเป็นสายก็ได้ 3 รูปแบบธุรกิจ (A: Invoice → Tax Invoice / B: Tax Invoice ตรง / C: รับมัดจำ Invoice → Tax Invoice) ตรงกับวิธีทำงานจริงของ SME B2B และธุรกิจโครงการ'],
      ['ข้อมูลเก็บที่ไหน? PDPA?', 'AWS Singapore + สำรองในไทย เลขผู้เสียภาษีและบัญชีธนาคารเข้ารหัส AES-256 มี API export/delete สำหรับ PDPA และเก็บ audit log 7 ปีตามกฎหมายภาษี'],
      ['ออกใบจากมือถือได้ไหม?', 'ได้ Mobile UI เป็น PWA ติดตั้งจากเบราว์เซอร์เข้า home screen ออฟไลน์ก็เก็บ draft รอซิงก์'],
      ['"Invoice Card QR" คืออะไร?', 'คือเอกลักษณ์ภาษีของคุณห่อใน QR เมื่อใครต้องออกใบให้คุณ สแกนครั้งเดียว Tax ID ที่อยู่ สาขา PromptPay กรอกอัตโนมัติ — มีเฉพาะ Invosly'],
      ['ยกเลิกได้ไหม?', 'ได้ ไม่มีสัญญา ไม่มีค่ายกเลิก ยกเลิกจาก Settings บัญชีจะกลายเป็น read-only ในรอบบิลถัดไป export ข้อมูลทั้งหมดได้'],
    ],
    cta_title:'เริ่มออกใบกำกับใน 60 วินาที', cta_sub:'สำหรับ SME ไทย UI 3 ภาษา PDPA ครบ PWA มือถือ', cta_btn:'สร้างบัญชีฟรี →',
    foot_tag:'ระบบใบกำกับอัจฉริยะ ตรงมาตรฐานสรรพากร โดยทีมที่ส่งของจริง',
    foot_product:'สินค้า',  foot_links_p:[['หน้าแรก','/'],['สินค้า','/product']],
    foot_company:'บริษัท',  foot_links_c:[['เกี่ยวกับ','#'],['Blog','#'],['ติดต่อ','#']],
    foot_help:'ช่วยเหลือ',   foot_links_h:[['Docs','#'],['FAQ','#'],['สถานะระบบ','#']],
    foot_legal:'นโยบายความเป็นส่วนตัว · ข้อกำหนด',
    foot_copy:'Invosly Co., Ltd. · สงวนลิขสิทธิ์',
  },
  zh: {
    page_title: 'Invosly — 专为泰国企业打造的智能开票系统',
    page_desc:  '3 步开出 Tax Invoice。内置 VAT 算法、WHT 凭证、e-Tax 合规。三语界面、移动端 PWA、完整 PDPA 合规。',
    nav_home: '首页', nav_product: '产品介绍',
    nav_signin: '登录', nav_signup: '免费开始',
    hero_badge: '符合泰国税务局 e-Tax 标准',
    hero_title_a: 'Smart & Simple', hero_title_b: '专为泰国企业打造的智能开票系统',
    hero_desc: '3 步开出 Tax Invoice。内置 VAT 算法,符合 e-Tax 合规要求,支持打印和一键分享。',
    hero_cta: '免费开始 →', hero_demo: '查看产品介绍',
    hero_note: '无需信用卡 · 随时取消 · 60 秒上手',
    trust_title: '泰国 SME 与在泰华人企业的共同选择',
    trust: [
      ['100%', 'RD Section 86 全字段'],
      ['6',    '种单据类型 — 报价 → 收据'],
      ['3',    '三语 TH / EN / 中'],
      ['256',  'AES 字段级加密'],
    ],
    bench_kicker: '为什么选 INVOSLY', bench_title: '企业选择我们的 5 大理由',
    benefits: [
      { ico:'✓', name:'完全合规', desc:'每张 Tax Invoice 全字段覆盖泰国 RD Section 86 强制要求。VAT 7% 双模式 + WHT 5 档(1/3/5/10/15%)+ PDF/A-1b 长期归档,全部内置。', bullets:['Section 86 全字段','VAT 含税/不含税切换','WHT 50 ทวิ 凭证','PDF/A-1b 归档格式'] },
      { ico:'⚡', name:'简单易用', desc:'30 秒开一张 Tax Invoice。选客户(或扫描对方 Invoice 名片 QR)、填金额、发送。无需会计知识。', bullets:['3 步开票流程','客户库自动联想','移动端优先 UI','零学习成本'] },
      { ico:'🔒', name:'数据安全', desc:'Tax ID、银行账号字段级 AES-256 加密。完整 PDPA 合规。泰国本地化部署。审计日志保留 7 年(符合税法)。', bullets:['AES-256 加密','PDPA 完全合规','泰国本地化(AWS 新加坡)','审计日志保 7 年'] },
      { ico:'🔄', name:'智能单据流转', desc:'完整的单据自动化引擎。6 种单据(报价单 / 发票 / 送货单 / 增值税发票 / 收据 / 预扣税凭证)一键转换链路。3 种业务流程模式(A/B/C)对应 SME / B2B / 工程项目的真实工作方式。', bullets:['6 种单据一条链路','一键 报价 → 发票 → 增值税发票','3 业务流程 (A/B/C) 适配 SME/B2B/工程','PDF 模板 frozen 保护历史单据'] },
      { ico:'🤖', name:'AI 税务工具', desc:'拍一张 50 ทวิ 凭证 — Claude Vision 自动识别付款方、收款方、收入类型、计税基数、WHT 税率。客户库与商品库自动 dedupe。多副本 PDF (Original × N + Copy × M) 一次渲染输出。', bullets:['WHT 凭证 OCR (Claude Vision)','客户 + 商品自动 dedupe','PDF 多副本 (Original / Copy)','发票号前缀按名片配置'] },
    ],
    how_kicker:'使用流程', how_title:'从注册到开出第一张发票 — 仅需 60 秒',
    how_steps: [
      ['免费注册','邮箱 + 密码,无需信用卡。系统自动为你创建账户、默认 Invoice 名片,以及第一个组织。'],
      ['添加客户','输入 Tax ID 一次(或扫描对方 QR 名片)— 公司名、地址、邮箱自动从客户库带出。'],
      ['开票 & 分享','输入商品行,点击发送。系统生成打印级 PDF,通过 Email、LINE 发送或下载。'],
    ],
    feat_kicker:'产品亮点', feat_title:'专为泰国企业的开票方式而生',
    feat: [
      ['📇','Invoice 名片 + QR','行业首创。把你的 Tax ID、地址、银行信息封装成 QR 码 — 客户扫一次,永不再手输。'],
      ['💸','PromptPay 内嵌','每张发票自带预填金额的 PromptPay QR 码。客户扫码即付,系统自动对账。'],
      ['📄','打印级 PDF','WeasyPrint 引擎 + Sarabun + Noto Sans Thai 字体。A4、300 DPI、字体内嵌 — 任何打印机都一致。'],
      ['🔍','公开溯源页','每张发票自带防伪 QR,链接到公开详情页 — 客户一键验真。'],
      ['📱','移动 + 桌面','手机 PWA、桌面完整 SPA、运营管理后台 — 同一份数据,实时同步。'],
      ['🛡','审计 & PDPA','所有操作留痕。一键数据导出 / 删除 API,满足 PDPA 数据主体请求。'],
    ],
    faq_kicker:'常见问题', faq_title:'常见问题',
    faqs: [
      ['Invosly 是否符合泰国税务局规定?', '完全符合。每张 Tax Invoice 模板都覆盖 RD Section 86 全部强制字段 — 泰英双语标题、买卖双方 Tax ID、分支机构、VAT 7% 行、原件/副本标识。同时支持 e-Tax Invoice by Email,可输出 PDF/A-1b 归档格式。'],
      ['必须按固定顺序开单吗?', '不用。6 种单据(报价单、发票、送货单、增值税发票、收据、预扣税凭证)可以独立创建,也可以串成链路。3 种业务流程(A: 发票 → 增值税发票 / B: 直接增值税发票 / C: 收预付款发票 → 增值税发票)对应 SME、B2B、工程项目企业的真实工作方式。'],
      ['数据存哪里?符合 PDPA 吗?', '托管在 AWS 新加坡,泰国本地有灾备。Tax ID 和银行账号字段级 AES-256 加密。提供一键数据导出 / 删除 API 满足 PDPA 数据主体请求。审计日志保留 7 年(符合税法)。'],
      ['可以用手机开票吗?', '可以。移动端是 PWA — 从浏览器添加到主屏。离线状态可保存草稿,联网后自动同步。'],
      ['"Invoice 名片 QR"是什么?', '它是把你的开票身份信息(Tax ID、地址、分支、PromptPay)封装成一个 QR 码。当客户需要给你开票时,扫一次即自动填充——行业首创,目前只有 Invosly 提供。'],
      ['可以随时取消吗?', '可以。无合同、无取消费用。在「设置」页一键取消,账户在下一个计费周期自动转为只读,可随时导出全部数据。'],
    ],
    cta_title:'60 秒开始你的第一张发票', cta_sub:'专为泰国 SME 打造。三语界面、完整 PDPA、移动端 PWA。', cta_btn:'创建免费账户 →',
    foot_tag:'符合泰国税务局合规要求的智能开票系统,由真正交付的团队打造。',
    foot_product:'产品',  foot_links_p:[['首页','/'],['产品介绍','/product']],
    foot_company:'公司',  foot_links_c:[['关于我们','#'],['博客','#'],['联系我们','#']],
    foot_help:'帮助',     foot_links_h:[['文档','#'],['FAQ','#'],['系统状态','#']],
    foot_legal:'隐私政策 · 服务条款',
    foot_copy:'Invosly Co., Ltd. · 保留所有权利',
  },
};
